import React from 'react';
import { TrendUp, TrendDown } from '@phosphor-icons/react';

export default function EmployeeYearlyStats({ employeeName, records, selectedYear, selectedMonth }) {
  // Use DOCHOD for statistics (not przelew)
  const calculateDochod = (record) => {
    return record.dochod || 0;
  };

  // Filter records for the selected year
  const yearRecords = records.filter(r => r.year === selectedYear);

  // Calculate total for the year (sum of dochod)
  const yearTotal = yearRecords.reduce((sum, r) => sum + calculateDochod(r), 0);

  // Count unique months with data
  const uniqueMonths = new Set(yearRecords.map(r => r.month));
  const monthsCount = uniqueMonths.size;

  // Calculate average
  const average = monthsCount > 0 ? yearTotal / monthsCount : 0;

  // Calculate current month total
  const currentMonthRecords = records.filter(
    r => r.month === selectedMonth && r.year === selectedYear
  );
  const currentMonthTotal = currentMonthRecords.reduce((sum, r) => sum + calculateDochod(r), 0);

  // Calculate previous month total
  const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
  const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
  const prevMonthRecords = records.filter(
    r => r.month === prevMonth && r.year === prevYear
  );
  const prevMonthTotal = prevMonthRecords.reduce((sum, r) => sum + calculateDochod(r), 0);

  // Calculate percentage change
  const percentChange = prevMonthTotal > 0
    ? ((currentMonthTotal - prevMonthTotal) / prevMonthTotal) * 100
    : 0;

  const hasChange = currentMonthTotal > 0 && prevMonthTotal > 0;

  return (
    <div className="bg-muted border-2 border-border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        {/* Employee name */}
        <div className="flex-1">
          <h3 className="font-heading text-lg font-bold text-blue-900 mb-1">
            {employeeName}
          </h3>
          <div className="text-xs text-muted-foreground">
            Statystyka za {selectedYear}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6">
          {/* Year total */}
          <div className="text-right">
            <div className="text-xs text-muted-foreground mb-1">Suma roczna</div>
            <div className="text-2xl font-bold text-blue-900">
              {yearTotal.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
            </div>
          </div>

          {/* Average */}
          <div className="text-right">
            <div className="text-xs text-muted-foreground mb-1">
              Średnia ({monthsCount} {monthsCount === 1 ? 'miesiąc' : monthsCount < 5 ? 'miesiące' : 'miesięcy'})
            </div>
            <div className="text-xl font-semibold text-foreground">
              {average.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
            </div>
          </div>

          {/* Percentage change */}
          {hasChange && (
            <div className="text-right min-w-[120px]">
              <div className="text-xs text-muted-foreground mb-1">Zmiana m/m</div>
              <div className={`flex items-center gap-1 justify-end text-lg font-bold ${
                percentChange >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {percentChange >= 0 ? (
                  <TrendUp className="w-5 h-5" weight="bold" />
                ) : (
                  <TrendDown className="w-5 h-5" weight="bold" />
                )}
                <span>{Math.abs(percentChange).toFixed(1)}%</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                vs {prevMonth}.{prevYear}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
